/**
 * useOrderValidation Hook
 * Handle order validation logic (stock, price, promo checks)
 */

import { useToast } from '@chakra-ui/react';
import { showToast } from '@diamond/shared/ui';
import { apiInstance } from '@diamond/shared/utils';
import type {
  OrderValidationResult,
  ProductChangeData,
  WarningType,
} from '@diamond/sol-buyer/utils';
import {
  handleCheckoutError,
  processPromoValidation,
} from '@diamond/sol-buyer/utils';
import { useState } from 'react';

export interface UseOrderValidationReturn {
  validateOrder: (metaId: string, token: string) => Promise<ValidationResult>;
  isValidating: boolean;
  validationError: string | null;
}

export interface ValidationResult {
  isValid: boolean;
  warningType: WarningType;
  warningMessage: string | null;
  productChanges: ProductChangeData[];
  invalidPromos: ReturnType<typeof processPromoValidation> | null;
}

/**
 * Custom hook untuk handle order validation
 *
 * @example
 * const { validateOrder, isValidating } = useOrderValidation();
 * const result = await validateOrder(metaId, token);
 */
export const useOrderValidation = (): UseOrderValidationReturn => {
  const toast = useToast();
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Wrapper function to match ToastFunction signature
  const toastFn = (
    _toast: any,
    status: 'success' | 'error' | 'warning' | 'info',
    message: string
  ) => {
    showToast(toast, status as any, message);
  };

  const validateOrder = async (
    metaId: string,
    token: string
  ): Promise<ValidationResult> => {
    setIsValidating(true);
    setValidationError(null);

    try {
      const response = await apiInstance(token).get<OrderValidationResult>(
        `/order/meta/${metaId}/order-validation`
      );

      const data = response.data;
      const processedInvalidPromos = processPromoValidation(
        data.invalid_promos as any
      );

      // Check validation result
      if (
        data.code === 'DIFF_PRICE_OR_STOCK' ||
        data.code === 'PARTIALLY_OUT_OF_STOCK'
      ) {
        return {
          isValid: false,
          warningType: 'price_stock_changes',
          warningMessage:
            'Terdapat perubahan harga atau kuantitas pada pesanan Anda',
          productChanges: data.data || [],
          invalidPromos: processedInvalidPromos,
        };
      }

      // Check invalid promos
      const hasInvalidPromo =
        processedInvalidPromos.hasDiscount ||
        processedInvalidPromos.hasFreeGoods ||
        processedInvalidPromos.hasCashback ||
        processedInvalidPromos.hasBundledCashback ||
        processedInvalidPromos.hasBundledDiscount ||
        processedInvalidPromos.hasBundledFreeGoods;

      if (hasInvalidPromo) {
        return {
          isValid: false,
          warningType: 'promo_invalid',
          warningMessage:
            'Promo berikut tidak bisa diproses karena telah kedaluarsa atau belum memenuhi syarat dan ketentuan yang berlaku.',
          productChanges: [],
          invalidPromos: processedInvalidPromos,
        };
      }

      // All validations passed
      return {
        isValid: true,
        warningType: null,
        warningMessage: null,
        productChanges: [],
        invalidPromos: processedInvalidPromos,
      };
    } catch (error: unknown) {
      setIsValidating(false);

      const errorCode =
        error instanceof Error && 'response' in error
          ? (error as any).response?.data?.error
          : undefined;
      const errorMessage =
        error instanceof Error && 'response' in error
          ? (error as any).response?.data?.message
          : undefined;
      const errorData =
        error instanceof Error && 'response' in error
          ? (error as any).response?.data?.data || []
          : [];

      // Handle different error types
      if (errorCode === 'ALL_ITEM_CANT_BE_PROCESSED') {
        return {
          isValid: false,
          warningType: 'no_product',
          warningMessage:
            'Mohon maaf, stok produk yang Anda pilih saat ini sedang kosong. Silakan cari produk serupa.',
          productChanges: errorData,
          invalidPromos: null,
        };
      }

      if (errorCode === 'MIN_ORDER_NOT_REACHED') {
        return {
          isValid: false,
          warningType: 'min_order',
          warningMessage: errorMessage || 'Minimum order belum tercapai',
          productChanges: errorData,
          invalidPromos: null,
        };
      }

      if (errorCode === 'MIN_ORDER_NOT_REACHED_DUE_TO_TOTAL_DIFF') {
        return {
          isValid: false,
          warningType: 'min_order_total_diff',
          warningMessage:
            'Ada barang yang mengalami perubahan harga atau stock sehingga tidak mencapai minimum order.',
          productChanges: errorData,
          invalidPromos: null,
        };
      }

      // Unknown error
      const result = handleCheckoutError(
        error,
        'validate-order',
        toastFn,
        toast,
        'Terjadi kesalahan validasi',
        metaId
      );
      setValidationError(result.message);

      throw error;
    } finally {
      setIsValidating(false);
    }
  };

  return {
    validateOrder,
    isValidating,
    validationError,
  };
};
